import { ApiAgentRequest, request } from "../utils/request"
import { useAuthQuery } from "./auth"

export enum DocumentProcessStatus {
  NOT_UPLOADED = 'NOT_UPLOADED', // Before S3 presigned url is used
  DOCUMENT_TYPE_VALIDATION_FAILED = 'DOCUMENT_TYPE_VALIDATION_FAILED',
  DOCUMENT_TYPE_VALIDATION_COMPLETED = 'DOCUMENT_TYPE_VALIDATION_COMPLETED',
  DOCUMENT_DATA_EXTRACTION_FAILED = 'DOCUMENT_DATA_EXTRACTION_FAILED',
  DOCUMENT_DATA_EXTRACTION_COMPLETED = 'DOCUMENT_DATA_EXTRACTION_COMPLETED',
  UPLOADED_NOT_PROCESSED = 'UPLOADED_NOT_PROCESSED',
  UPLOADED_AI_SERVICE_UNAVAILABLE = 'UPLOADED_AI_SERVICE_UNAVAILABLE',
}

export enum ConfidenceLevel {
  HIGH = 'high',
  LOW = 'low',
  MISMATCH = 'mismatch',
  NONE = 'none',
}

type ValueWithConfidence<T> = {
  value: T
  confidence: ConfidenceLevel
}

type PassportExtraction = {
  firstName?: ValueWithConfidence<string>
  familyName?: ValueWithConfidence<string>
  middleName?: ValueWithConfidence<string>
  passportNumber?: ValueWithConfidence<string>
  expiryDate?: ValueWithConfidence<string>
  nationality?: ValueWithConfidence<string>
  birthDate?: ValueWithConfidence<string>
  sex?: ValueWithConfidence<string>
  address?: {
    address?: string
    city?: string
    province?: string
    postalCode?: string
    country?: string
    confidence?: ConfidenceLevel
  }
}

export type Passport = {
  id: string
  type: 'passport'
  attributes: {
    passportNumber?: string
    expiryDate?: string
    fileName: string
    nationality?: string
    documentProcessStatus?: DocumentProcessStatus
    uploadedAt: string
    createdAt?: string
    processingResults?: {
      documentTypeConfidence?: ConfidenceLevel
      extractedData?: PassportExtraction
    }
  }
}

export type RawGetPassportResponse = {
  data: Passport
}

export const isPassportProcessingComplete = (passport?: Passport, hasAiProcessingTimedOut?: boolean) => {
  if (hasAiProcessingTimedOut) {
    return true
  }
  const status = passport?.attributes?.documentProcessStatus;
  return status && [
    DocumentProcessStatus.DOCUMENT_DATA_EXTRACTION_COMPLETED,
    DocumentProcessStatus.DOCUMENT_DATA_EXTRACTION_FAILED,
    DocumentProcessStatus.DOCUMENT_TYPE_VALIDATION_FAILED,
    DocumentProcessStatus.UPLOADED_NOT_PROCESSED,
    DocumentProcessStatus.UPLOADED_AI_SERVICE_UNAVAILABLE,
  ].includes(status);
};

type GetPassportProps = {
  studentId: string | undefined
  passportId: string | undefined
  setHasAiProcessingTimedOut: (hasAiProcessingTimedOut: boolean) => void
  configTimeout?: number
  refetchCondition?: (data: Passport | undefined) => boolean
  enabled?: boolean
}

export function useGetPassport(props: GetPassportProps) {
  const { isLoading, isFetching, data } = useAuthQuery(
    ['passport', props.studentId, props.passportId],
    async () => await getPassport(props.studentId, props.passportId),
    {
      enabled: props.enabled,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchInterval: ({
        state,
      }: {
        state: { dataUpdatedAt: number; data?: { data?: Passport }; dataUpdateCount: number }
      }) => {
        const configTimeout = props.configTimeout || 45000
        const elapsedTime = state.dataUpdatedAt > 0 ? Date.now() - state.dataUpdatedAt : 0
        const hasExceededMaxTime = state.dataUpdatedAt > 0 && elapsedTime > configTimeout

        if (hasExceededMaxTime) {
          props.setHasAiProcessingTimedOut(true);
          return false
        }

        if (isPassportProcessingComplete(state.data?.data)) {
          return false
        }
        return 5000
      },
    },
  )

  return {
    isLoadingPassport: isLoading,
    isFetchingPassport: isFetching,
    passport: data?.data,
  }
}

export async function getPassport(studentId?: string, passportId?: string) {
  return request<RawGetPassportResponse>(
    new ApiAgentRequest(`/students/${studentId}/passports/${passportId}`, {
      method: 'GET',
    }),
    {
      isExpectedResponse: (res): res is RawGetPassportResponse => res as boolean,
    },
  )
}
